import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getEnrollmentLink, startEnrollmentCheckout } from "../services/commerceService";
import "./PackagePaymentPage.css";
import { paymentLinkErrorMessage } from "./paymentLinkErrors";

const money = (minor, currency) => new Intl.NumberFormat("en-US", { style: "currency", currency }).format((minor || 0) / 100);
const expiration = (value) => value ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : null;

export default function PackagePaymentPage() {
  const { token } = useParams();
  const [query] = useSearchParams();
  const result = query.get("result");
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () => getEnrollmentLink(token).then((data) => { if (active) setEnrollment(data); }).catch((cause) => { if (active) setError(paymentLinkErrorMessage(cause)); });
    load();
    const interval = result === "success" ? window.setInterval(load, 3000) : null;
    return () => { active = false; if (interval) window.clearInterval(interval); };
  }, [token, result]);

  const checkout = async () => {
    setBusy(true); setError("");
    try {
      const root = `${window.location.origin}/pay/${token}`;
      const response = await startEnrollmentCheckout(token, { successUrl: `${root}?result=success`, cancelUrl: `${root}?result=cancel` });
      window.location.assign(response.redirect_url);
    } catch (cause) {
      setError(paymentLinkErrorMessage(cause, true));
      setBusy(false);
    }
  };

  if (!enrollment && !error) return <main className="package-pay"><section><div className="package-pay__spinner" /><p>Opening secure checkout…</p></section></main>;
  if (error) return <main className="package-pay"><section className="package-pay__error"><h1>We couldn’t open this payment link</h1><p>{error}</p><small>Contact your gym or coach if you still need help.</small></section></main>;
  const organizationName = enrollment.organization?.name?.trim() || "Your gym";
  if (result === "success") return <main className="package-pay"><section><div className="package-pay__check">✓</div><span className="package-pay__eyebrow">{organizationName}</span><h1>{enrollment.status === "active" ? "You’re enrolled" : "Payment submitted"}</h1><p>{enrollment.status === "active" ? `${enrollment.package.name} is now active.` : "Stripe is confirming your payment. Your package will become available automatically after confirmation."}</p><small>You may safely close this page.</small></section></main>;

  return <main className="package-pay"><section>
    <div className="package-pay__lock" aria-hidden="true">$</div>
    <span className="package-pay__eyebrow">Secure checkout for {organizationName}</span>
    <h1>Complete your enrollment</h1>
    <p className="package-pay__intro">Review your package details before continuing to Stripe.</p>
    <div className="package-pay__package"><strong>{enrollment.package.name}</strong>{enrollment.package.description && <span>{enrollment.package.description}</span>}</div>
    <div className="package-pay__price"><strong>{money(enrollment.package.amount_minor, enrollment.package.currency)}</strong><span>{enrollment.package.billing_model === "one_time" ? "one time" : `per ${enrollment.package.billing_interval}`}</span></div>
    <dl><div><dt>Customer</dt><dd>{enrollment.customer.name || enrollment.customer.email}</dd></div>{enrollment.package.sessions_per_cycle && <div><dt>Included</dt><dd>{enrollment.package.sessions_per_cycle} sessions</dd></div>}{enrollment.use_trial && <div><dt>Trial</dt><dd>{enrollment.trial_days} days</dd></div>}</dl>
    {result === "cancel" && <div className="package-pay__notice">Checkout was closed and no package was activated. You can try again.</div>}
    <button disabled={busy || enrollment.status === "active"} onClick={checkout}>{busy ? "Opening Stripe…" : enrollment.status === "active" ? "Already enrolled" : "Continue to secure payment"}</button>
    <small>This private link is for {enrollment.customer.name || enrollment.customer.email}{expiration(enrollment.expires_at) ? ` and expires ${expiration(enrollment.expires_at)}` : ""}. Payment details are collected and secured by Stripe.</small>
  </section></main>;
}
